import React from "react";
import styles from "./styles.module.scss";
import { useTranslation } from "react-i18next";
import { schedulingFetchPatch } from "app/utils/request/scheduling/fetch";
import { toast } from "react-toastify";
import ButtonVariant from "app/components/Button/ButtonVariant";
import { DateTime } from "luxon";
import { formatMonthDayWithYearIfNeeded } from "app/utils/luxonFormats";

interface Offering {
  offeringTitle: string;
  remainingSessions: number;
  creditBundleIds?: number[];
  // CN-2641: when a package's credits expire (soonest stack first). Null when
  // the offering grants credits that never expire.
  expiredAt?: string | null;
}

interface OfferingCardProps {
  subscription: Offering;
  remaining?: number;
  updateRemainingSessions?: (
    purchaseId: string,
    incrementValue: number
  ) => void;
  purchaseId?: string;
  isCoach?: boolean;
  onCompleted?: (subId: number) => void;
}

export default function OfferingCard({
  subscription,
  isCoach = false,
  onCompleted,
}: OfferingCardProps) {
  const { t } = useTranslation();

  const handleMarkAsCompleted = async (creditBundleId: number) => {
    try {
      await schedulingFetchPatch(`/credit_bundles/${creditBundleId}/mark_as_completed`);

      onCompleted(creditBundleId);
      toast.success(t("Marked as completed"));

    } catch (error) {
      toast.error(t("Something went wrong"));
      console.error("Mark as completed failed:", error);
    }
  };

  return (
    <div
      className={
        isCoach ? styles.subscriptionCardCoach : styles.subscriptionCard
      }
    >
      <div className={styles.subscriptionInfo}>
        <div
          className={`${styles.iconWrapper2} ${
            isCoach ? styles.iconWrapperMargin : ""
          }`}
        >
          <i className="tab-icon ico ico-dollar-circle" />
        </div>
        <div className={styles.details}>
          <p className={styles.subscriptionName}>{subscription.offeringTitle}</p>
          <div className={styles.subscriptionRow}>
            <p className={styles.subscriptionDetails}>{t("One time payment")}</p>
            {/* CN-2641: a package with a relative expiry has a known last day. */}
            {subscription.expiredAt && (
              <>
                <span className={styles.dotSeparator}>•</span>
                <p className={styles.subscriptionDetails}>
                  {t("Credits expire on {{date}}", {
                    date: formatMonthDayWithYearIfNeeded(
                      DateTime.fromISO(subscription.expiredAt),
                    ),
                  })}
                </p>
              </>
            )}
          </div>
        </div>
      </div>
      {isCoach && (
        <div className={styles.remainingSessionsContainer}>
          <ButtonVariant
            title={t('Mark as Completed')}
            buttonType='primary-outline'
            onClickFunc={() => handleMarkAsCompleted(subscription.creditBundleIds[0])}
            buttonStyles={styles.markCompletedButton}
          />
        </div>
      )}
    </div>
  );
}
